import type { FormEvent, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ModelGender,
  ModelOnboardingChecklistStatus,
  ModelOnboardingIntent,
  ModelOnboardingState,
  ModelOnboardingStep,
  ModelPartnerInviteMode,
} from '@fetmode/types';
import { MODEL_GENDERS, MODEL_ONBOARDING_INTENTS } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { resolveAppUrl, useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { formatUsername } from '@fetmode/utils';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { useStudioModel } from '../studio/index.js';
import { ApplicationIdentityVendor } from './ApplicationIdentityVendor.js';
import './application-steps.css';
import './application-identity-vendor.css';

type ApplicationPane =
  | 'account'
  | 'intent'
  | 'act'
  | 'legalName'
  | 'dateOfBirth'
  | 'idDocument'
  | 'signature'
  | 'displayName'
  | 'gender'
  | 'review';

/** Individual genders only — couples register separately, then form an act later. */
const APPLICATION_GENDERS = MODEL_GENDERS.filter((value) => value !== 'COUPLE');

const GENDER_LABELS: Record<ModelGender, string> = {
  WOMAN: 'common.studio.live.genderWoman',
  MAN: 'common.studio.live.genderMan',
  TRANS: 'common.studio.live.genderTrans',
  COUPLE: 'common.studio.live.genderCouple',
  NONBINARY: 'common.studio.live.genderNonbinary',
};

/** Couples are created after each person qualifies — not chosen as an application intent. */
const APPLICATION_INTENTS = MODEL_ONBOARDING_INTENTS.filter((value) => value !== 'COUPLE');

const INTENT_COPY: Record<ModelOnboardingIntent, { title: string; hint: string }> = {
  SOLO: {
    title: 'common.studio.onboarding.intentSolo',
    hint: 'common.studio.onboarding.intentSoloHint',
  },
  COUPLE: {
    title: 'common.studio.onboarding.intentCouple',
    hint: 'common.studio.onboarding.intentCoupleHint',
  },
  GROUP: {
    title: 'common.studio.onboarding.intentGroup',
    hint: 'common.studio.onboarding.intentGroupHint',
  },
  JOIN_ACT: {
    title: 'common.studio.onboarding.intentJoin',
    hint: 'common.studio.onboarding.intentJoinHint',
  },
};

const MENU_LABELS: Record<Exclude<ApplicationPane, 'act'>, string> = {
  account: 'common.studio.onboarding.rowAccount',
  intent: 'common.studio.onboarding.rowIntent',
  legalName: 'common.studio.onboarding.legalName',
  dateOfBirth: 'common.studio.onboarding.dob',
  idDocument: 'common.studio.onboarding.rowId',
  signature: 'common.studio.onboarding.rowSignature',
  displayName: 'common.studio.live.displayName',
  gender: 'common.studio.live.gender',
  review: 'common.studio.onboarding.rowReview',
};

const MENU_ORDER: Exclude<ApplicationPane, 'act'>[] = [
  'account',
  'intent',
  'legalName',
  'dateOfBirth',
  'idDocument',
  'signature',
  'displayName',
  'gender',
  'review',
];

const PILL_LABELS = {
  ok: 'common.studio.onboarding.statusOk',
  missing: 'common.studio.onboarding.statusMissing',
  rejected: 'common.studio.onboarding.statusRejected',
} as const;

interface OnboardingForm {
  pane: ApplicationPane;
  intent: ModelOnboardingIntent;
  legalFirstName: string;
  legalMiddleName: string;
  legalLastName: string;
  dateOfBirth: string;
  aliases: string;
  identityVerified: boolean;
  signatureName: string;
  agreementAccepted: boolean;
  taxAttested: boolean;
  actHandleDraft: string;
  partnerInviteMode: ModelPartnerInviteMode;
  joinActCode: string;
  displayName: string;
  gender: ModelGender | '';
}

function needsActStep(intent: ModelOnboardingIntent): boolean {
  return intent === 'COUPLE' || intent === 'GROUP' || intent === 'JOIN_ACT';
}

function persistStep(pane: ApplicationPane): ModelOnboardingStep {
  if (pane === 'account' || pane === 'intent') {
    return 'intent';
  }
  if (pane === 'act') {
    return 'act';
  }
  if (pane === 'displayName' || pane === 'gender') {
    return 'profile';
  }
  if (pane === 'review') {
    return 'review';
  }
  return 'qualify';
}

function initialPane(state: ModelOnboardingState): ApplicationPane {
  if (state.status === 'APPROVED') {
    return 'review';
  }
  if (state.step === 'intent') {
    return 'intent';
  }
  if (state.step === 'act') {
    return 'act';
  }
  if (state.step === 'qualify') {
    if (!state.legalFirstName?.trim() || !state.legalLastName?.trim()) {
      return 'legalName';
    }
    if (!state.dateOfBirth) {
      return 'dateOfBirth';
    }
    if (!state.identityVerifiedAt) {
      return 'idDocument';
    }
    return 'signature';
  }
  if (state.step === 'profile') {
    return state.gender ? 'gender' : 'displayName';
  }
  return 'review';
}

function nextPane(pane: ApplicationPane, intent: ModelOnboardingIntent): ApplicationPane {
  if (pane === 'account') {
    return 'intent';
  }
  if (pane === 'intent') {
    return needsActStep(intent) ? 'act' : 'legalName';
  }
  if (pane === 'act') {
    return 'legalName';
  }
  if (pane === 'legalName') {
    return 'dateOfBirth';
  }
  if (pane === 'dateOfBirth') {
    return 'idDocument';
  }
  if (pane === 'idDocument') {
    return 'signature';
  }
  if (pane === 'signature') {
    return 'displayName';
  }
  if (pane === 'displayName') {
    return 'gender';
  }
  return 'review';
}

function previousPane(pane: ApplicationPane, intent: ModelOnboardingIntent): ApplicationPane {
  if (pane === 'review') {
    return 'gender';
  }
  if (pane === 'gender') {
    return 'displayName';
  }
  if (pane === 'displayName') {
    return 'signature';
  }
  if (pane === 'signature') {
    return 'idDocument';
  }
  if (pane === 'idDocument') {
    return 'dateOfBirth';
  }
  if (pane === 'dateOfBirth') {
    return 'legalName';
  }
  if (pane === 'legalName') {
    return needsActStep(intent) ? 'act' : 'intent';
  }
  if (pane === 'act') {
    return 'intent';
  }
  return 'account';
}

function menuStatus(
  key: Exclude<ApplicationPane, 'act'>,
  state: ModelOnboardingState,
  form: OnboardingForm,
): ModelOnboardingChecklistStatus {
  const rejected = state.status === 'REJECTED';
  if (key === 'account' || key === 'intent') {
    return 'ok';
  }
  if (key === 'legalName') {
    return form.legalFirstName.trim() && form.legalLastName.trim()
      ? 'ok'
      : rejected
        ? 'rejected'
        : 'missing';
  }
  if (key === 'dateOfBirth') {
    return form.dateOfBirth ? 'ok' : rejected ? 'rejected' : 'missing';
  }
  if (key === 'idDocument') {
    return form.identityVerified ? 'ok' : rejected ? 'rejected' : 'missing';
  }
  if (key === 'signature') {
    return form.agreementAccepted && form.signatureName.trim() ? 'ok' : rejected ? 'rejected' : 'missing';
  }
  if (key === 'displayName') {
    return form.displayName.trim() ? 'ok' : rejected ? 'rejected' : 'missing';
  }
  if (key === 'gender') {
    return form.gender ? 'ok' : rejected ? 'rejected' : 'missing';
  }
  return state.status === 'APPROVED' ? 'ok' : rejected ? 'rejected' : 'missing';
}

function composedLegalName(form: Pick<OnboardingForm, 'legalFirstName' | 'legalMiddleName' | 'legalLastName'>): string {
  return [form.legalFirstName, form.legalMiddleName, form.legalLastName]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');
}

function formFromState(
  state: ModelOnboardingState,
  username: string,
  pane?: ApplicationPane,
  account?: { firstName?: string | null; middleName?: string | null; lastName?: string | null },
): OnboardingForm {
  const intent = state.intent === 'COUPLE' ? 'SOLO' : state.intent;
  const gender = state.gender === 'COUPLE' ? '' : (state.gender ?? '');
  return {
    pane: pane ?? initialPane(state),
    intent,
    legalFirstName: state.legalFirstName ?? account?.firstName ?? '',
    legalMiddleName: state.legalMiddleName ?? account?.middleName ?? '',
    legalLastName: state.legalLastName ?? account?.lastName ?? '',
    dateOfBirth: state.dateOfBirth ?? '',
    aliases: state.aliases ?? '',
    identityVerified: Boolean(state.identityVerifiedAt),
    signatureName: state.signatureName ?? '',
    agreementAccepted: Boolean(state.agreementSignedAt),
    taxAttested: Boolean(state.taxAttestedAt),
    actHandleDraft: state.actHandleDraft ?? '',
    partnerInviteMode: state.partnerInviteMode ?? 'LATER',
    joinActCode: state.joinActCode ?? '',
    displayName: state.displayName ?? username,
    gender,
  };
}

export function ApplicationStepsPage(): ReactElement {
  const { api, user, applySession } = useAuth();
  const { refresh } = useStudioModel();
  const navigate = useNavigate();
  const [state, setState] = useState<ModelOnboardingState | null>(null);
  const [form, setForm] = useState<OnboardingForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const consumerBase = resolveAppUrl(
    'consumer',
    import.meta.env as Record<string, string | undefined>,
  ).replace(/\/$/, '');
  const modelAgreementUrl = `${consumerBase}/p/terms`;
  const recordKeepingUrl = `${consumerBase}/p/2257`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getModelOnboarding();
      setState(result.onboarding);
      setForm(formFromState(result.onboarding, user?.username ?? '', undefined, user ?? undefined));
      await refresh({ quiet: true });
    } catch {
      setError(t('common.studio.onboarding.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, user, refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  const payload = useMemo(() => {
    if (!form) {
      return null;
    }
    return {
      step: persistStep(form.pane),
      intent: form.intent,
      legalFirstName: form.legalFirstName,
      legalMiddleName: form.legalMiddleName,
      legalLastName: form.legalLastName,
      dateOfBirth: form.dateOfBirth,
      aliases: form.aliases,
      signatureName: form.signatureName,
      agreementAccepted: form.agreementAccepted,
      taxAttested: form.taxAttested,
      actHandleDraft: form.actHandleDraft,
      partnerInviteMode: form.partnerInviteMode,
      joinActCode: form.joinActCode,
      displayName: form.displayName.trim() || undefined,
      gender: form.gender || undefined,
    };
  }, [form]);

  async function save(next?: ApplicationPane): Promise<boolean> {
    if (!payload || !form) {
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.updateModelOnboarding({
        ...payload,
        step: persistStep(next ?? form.pane),
      });
      if (result.session) {
        applySession(result.session);
      }
      setState(result.onboarding);
      setForm({
        ...formFromState(result.onboarding, user?.username ?? '', next ?? form.pane, user ?? undefined),
        pane: next ?? form.pane,
      });
      await refresh({ quiet: true });
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.onboarding.saveError'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleContinue(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) {
      return;
    }
    await save(nextPane(form.pane, form.intent));
  }

  async function handleSubmit(): Promise<void> {
    if (!form) {
      return;
    }
    const saved = await save('review');
    if (!saved) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.submitModelOnboarding();
      if (result.session) {
        applySession(result.session);
      }
      setState(result.onboarding);
      setForm(formFromState(result.onboarding, user?.username ?? '', 'review', user ?? undefined));
      await refresh({ quiet: true });
      if (result.onboarding.canBroadcast || result.onboarding.status === 'APPROVED') {
        navigate('/settings/overview', { replace: true });
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.onboarding.submitError'));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyIdentity(): Promise<void> {
    if (!form) {
      return;
    }
    const saved = await save(form.pane);
    if (!saved) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.verifyModelIdentity();
      if (result.session) {
        applySession(result.session);
      }
      setState(result.onboarding);
      setForm(formFromState(result.onboarding, user?.username ?? '', form.pane, user ?? undefined));
      await refresh({ quiet: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.onboarding.saveError'));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !form || !state) {
    return <p className="application-steps__hint">{t('common.studio.dashboard.loading')}</p>;
  }

  if (state.status === 'APPROVED' && state.canBroadcast) {
    return <Navigate to="/settings/overview" replace />;
  }

  const handle = formatUsername(user?.username ?? form.displayName);

  return (
    <section className="application-steps" data-steps="split-v1">
      <aside className="fm-card application-steps__nav">
        <h2>{t('common.studio.onboarding.checklistTitle')}</h2>
        <ul className="application-steps__check">
          {MENU_ORDER.map((key) => {
            const status = menuStatus(key, state, form);
            return (
              <li key={key}>
                <button
                  type="button"
                  className={`application-steps__check-item${
                    form.pane === key ? ' application-steps__check-item--current' : ''
                  }`}
                  onClick={() => {
                    setForm({ ...form, pane: key });
                  }}
                >
                  <span>{t(MENU_LABELS[key])}</span>
                  <span className={`application-steps__pill application-steps__pill--${status}`}>
                    {t(PILL_LABELS[status])}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <div>
        <div className="application-steps__intro">
          <h1>{t('common.studio.onboarding.title')}</h1>
          <p>{t('common.studio.onboarding.lede')}</p>
        </div>
        {state.rejectionReason ? (
          <p className="fm-error">
            {t('common.studio.onboarding.rejected').replace('{{reason}}', state.rejectionReason)}
          </p>
        ) : null}
        {error ? <p className="fm-error">{error}</p> : null}

        <form
          className="fm-card application-steps__form"
          style={{ marginTop: '1rem' }}
          onSubmit={(event) => void handleContinue(event)}
        >
          {form.pane === 'account' ? (
            <>
              <h2>{t('common.studio.onboarding.stepAccount')}</h2>
              <p>{t('common.studio.onboarding.stepAccountHint')}</p>
              <p className="application-steps__note">
                {t('common.studio.onboarding.stepAccountReady').replace('{{handle}}', handle)}
              </p>
            </>
          ) : null}

          {form.pane === 'intent' ? (
            <>
              <h2>{t('common.studio.onboarding.stepIntent')}</h2>
              <p>{t('common.studio.onboarding.stepIntentHint')}</p>
              <div className="application-steps__intents">
                {APPLICATION_INTENTS.map((intent) => (
                  <button
                    key={intent}
                    type="button"
                    className={`application-steps__intent${form.intent === intent ? ' application-steps__intent--on' : ''}`}
                    onClick={() => {
                      setForm({ ...form, intent });
                    }}
                  >
                    <strong>{t(INTENT_COPY[intent].title)}</strong>
                    <span>{t(INTENT_COPY[intent].hint)}</span>
                  </button>
                ))}
              </div>
              <p className="application-steps__note">{t('common.studio.onboarding.agencyParked')}</p>
            </>
          ) : null}

          {form.pane === 'act' ? (
            <>
              <h2>{t('common.studio.onboarding.stepAct')}</h2>
              <p>{t('common.studio.onboarding.stepActHint')}</p>
              {form.intent === 'JOIN_ACT' ? (
                <label className="application-steps__field">
                  {t('common.studio.onboarding.joinCode')}
                  <input
                    className="fm-input"
                    value={form.joinActCode}
                    onChange={(event) => {
                      setForm({ ...form, joinActCode: event.target.value });
                    }}
                  />
                  <span className="application-steps__hint">
                    {t('common.studio.onboarding.joinCodeHint')}
                  </span>
                </label>
              ) : (
                <>
                  <label className="application-steps__field">
                    {t('common.studio.onboarding.inviteLater')}
                    <select
                      className="fm-input"
                      value={form.partnerInviteMode}
                      onChange={(event) => {
                        setForm({
                          ...form,
                          partnerInviteMode: event.target.value as ModelPartnerInviteMode,
                        });
                      }}
                    >
                      <option value="LATER">{t('common.studio.onboarding.inviteLater')}</option>
                      <option value="TOGETHER">{t('common.studio.onboarding.inviteTogether')}</option>
                    </select>
                  </label>
                  {form.partnerInviteMode === 'TOGETHER' ? (
                    <p className="application-steps__note">
                      {t('common.studio.onboarding.inviteTogetherHint')}
                    </p>
                  ) : null}
                  <label className="application-steps__field">
                    {t('common.studio.onboarding.actHandle')}
                    <input
                      className="fm-input"
                      value={form.actHandleDraft}
                      onChange={(event) => {
                        setForm({ ...form, actHandleDraft: event.target.value });
                      }}
                    />
                    <span className="application-steps__hint">
                      {t('common.studio.onboarding.actHandleHint')}
                    </span>
                  </label>
                </>
              )}
            </>
          ) : null}

          {form.pane === 'legalName' ? (
            <>
              <h2>{t('common.studio.onboarding.legalName')}</h2>
              <p>{t('common.studio.onboarding.stepIdentityHint')}</p>
              <div className="application-identity__names">
                <label className="application-steps__field">
                  {t('common.auth.firstName')}
                  <input
                    className="fm-input"
                    autoComplete="given-name"
                    value={form.legalFirstName}
                    onChange={(event) => {
                      setForm({ ...form, legalFirstName: event.target.value });
                    }}
                  />
                </label>
                <label className="application-steps__field">
                  {t('common.auth.middleName')}
                  <input
                    className="fm-input"
                    autoComplete="additional-name"
                    value={form.legalMiddleName}
                    onChange={(event) => {
                      setForm({ ...form, legalMiddleName: event.target.value });
                    }}
                  />
                </label>
                <label className="application-steps__field">
                  {t('common.auth.lastName')}
                  <input
                    className="fm-input"
                    autoComplete="family-name"
                    value={form.legalLastName}
                    onChange={(event) => {
                      setForm({ ...form, legalLastName: event.target.value });
                    }}
                  />
                </label>
              </div>
              <label className="application-steps__field">
                {t('common.studio.onboarding.aliases')}
                <input
                  className="fm-input"
                  value={form.aliases}
                  onChange={(event) => {
                    setForm({ ...form, aliases: event.target.value });
                  }}
                />
                <span className="application-steps__hint">
                  {t('common.studio.onboarding.aliasesHint')}
                </span>
              </label>
            </>
          ) : null}

          {form.pane === 'dateOfBirth' ? (
            <>
              <h2>{t('common.studio.onboarding.dob')}</h2>
              <p>{t('common.studio.onboarding.stepIdentityHint')}</p>
              <label className="application-steps__field">
                {t('common.studio.onboarding.dob')}
                <input
                  className="fm-input"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(event) => {
                    setForm({ ...form, dateOfBirth: event.target.value });
                  }}
                />
              </label>
            </>
          ) : null}

          {form.pane === 'idDocument' ? (
            <ApplicationIdentityVendor
              verified={form.identityVerified}
              busy={busy}
              onVerify={() => {
                void handleVerifyIdentity();
              }}
            />
          ) : null}

          {form.pane === 'signature' ? (
            <>
              <h2>{t('common.studio.onboarding.stepSignature')}</h2>
              <p>{t('common.studio.onboarding.stepSignatureHint')}</p>
              <label className="application-steps__field">
                {t('common.studio.onboarding.signature')}
                <input
                  className="fm-input"
                  value={form.signatureName}
                  onChange={(event) => {
                    setForm({ ...form, signatureName: event.target.value });
                  }}
                />
              </label>
              <label className="application-steps__field" style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={form.agreementAccepted}
                  onChange={(event) => {
                    setForm({ ...form, agreementAccepted: event.target.checked });
                  }}
                />
                <span>
                  {t('common.studio.onboarding.agreementBefore')}{' '}
                  <a href={modelAgreementUrl} target="_blank" rel="noreferrer">
                    {t('common.studio.onboarding.agreementModel')}
                  </a>
                  {` ${t('common.studio.onboarding.agreementAnd')} `}
                  <a href={recordKeepingUrl} target="_blank" rel="noreferrer">
                    {t('common.studio.onboarding.agreement2257')}
                  </a>
                  .
                </span>
              </label>
              <label className="application-steps__field" style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={form.taxAttested}
                  onChange={(event) => {
                    setForm({ ...form, taxAttested: event.target.checked });
                  }}
                />
                <span>{t('common.studio.onboarding.taxAttest')}</span>
              </label>
            </>
          ) : null}

          {form.pane === 'displayName' ? (
            <>
              <h2>{t('common.studio.live.displayName')}</h2>
              <p>{t('common.studio.onboarding.stepProfileHint')}</p>
              <label className="application-steps__field">
                {t('common.studio.live.displayName')}
                <input
                  className="fm-input"
                  value={form.displayName}
                  onChange={(event) => {
                    setForm({ ...form, displayName: event.target.value });
                  }}
                />
              </label>
            </>
          ) : null}

          {form.pane === 'gender' ? (
            <>
              <h2>{t('common.studio.live.gender')}</h2>
              <p>{t('common.studio.live.genderHint')}</p>
              <label className="application-steps__field">
                {t('common.studio.live.gender')}
                <select
                  className="fm-input"
                  value={form.gender}
                  onChange={(event) => {
                    setForm({ ...form, gender: event.target.value as ModelGender | '' });
                  }}
                >
                  <option value="">{t('common.studio.live.genderSelect')}</option>
                  {APPLICATION_GENDERS.map((value) => (
                    <option key={value} value={value}>
                      {t(GENDER_LABELS[value])}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}

          {form.pane === 'review' ? (
            <>
              <h2>{t('common.studio.onboarding.stepReview')}</h2>
              <p>{t('common.studio.onboarding.stepReviewHint')}</p>
              <ul className="application-steps__summary">
                <li>
                  <span>{t('common.studio.onboarding.rowIntent')}</span>
                  <strong>{t(INTENT_COPY[form.intent].title)}</strong>
                </li>
                <li>
                  <span>{t('common.studio.onboarding.legalName')}</span>
                  <strong>{composedLegalName(form) || '—'}</strong>
                </li>
                <li>
                  <span>{t('common.studio.onboarding.dob')}</span>
                  <strong>{form.dateOfBirth || '—'}</strong>
                </li>
                <li>
                  <span>{t('common.studio.live.displayName')}</span>
                  <strong>{form.displayName || '—'}</strong>
                </li>
                <li>
                  <span>{t('common.studio.live.gender')}</span>
                  <strong>{form.gender ? t(GENDER_LABELS[form.gender]) : '—'}</strong>
                </li>
              </ul>
            </>
          ) : null}

          <div className="application-steps__actions">
            {form.pane !== 'account' ? (
              <Button
                type="button"
                variant="secondary"
                label={t('common.studio.onboarding.back')}
                disabled={busy}
                onClick={() => {
                  setForm({ ...form, pane: previousPane(form.pane, form.intent) });
                }}
              />
            ) : null}
            {form.pane === 'review' ? (
              <Button
                type="button"
                label={busy ? t('common.studio.onboarding.saving') : t('common.studio.onboarding.submit')}
                disabled={busy}
                onClick={() => {
                  void handleSubmit();
                }}
              />
            ) : (
              <Button
                type="submit"
                label={busy ? t('common.studio.onboarding.saving') : t('common.studio.onboarding.continue')}
                disabled={busy}
              />
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
